"use client";
import React, { createContext, useContext, useState, useEffect } from "react";
import { useGetUserAccountBalances } from "@/api/userApi";
import { useQueryClient } from "@tanstack/react-query";

interface User {
  email: string;
  first_name: string;
  last_name: string;
  unique_key: string;
  profile_image: string;
  trading_challenge: string;
  description: string;
  is_active: boolean;
  user_type: string;
  meta_data: string;
  created_at: string;
}

interface Preference {
  country: string;
  trading_yoe: string;
  risk_class: string;
  language: string;
  asset_class: string;
  notification_preference: string;
  nudges_tracker: string;
  walkthrough_status: string;
  webhook: string;
  send_email: string;
  socials: string;
}

interface UserData {
  user: User;
  preference: Preference;
  is_old_user: boolean;
  user_id: string;
  hmac: string;
  token: string;
  subscription: string;
  start_date: string;
  redirect_after_login: string;
}

interface UserContextType {
  user: UserData | null;
  setUser: (user: UserData | null) => void;
  connectedBrokers: BrokerDetails;
  refreshConnectedBrokers: () => Promise<void>;
  balanceLoading: boolean;
  userLoading: boolean;
}
interface BrokerDetails {
  [brokerId: number]: {
    broker_id: number;
    broker_name: string;
    broker_image: string | null;
    type: string;
    balance: number;
    payload: {
      margin: Record<string, number | string>;
      main: Record<string, number | string>;
      trading: Record<string, number | string>;
    };
  };
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUserState] = useState<UserData | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const {
    data: userAccountData,
    isLoading: balanceLoading,
    isSuccess,
  } = useGetUserAccountBalances(user?.user_id);
  const [connectedBrokers, setConnectedBrokers] = useState<BrokerDetails>({});
  const queryClient = useQueryClient();
  const setUser = (newUser: UserData | null) => {
    setUserState(newUser);
    if (typeof window !== "undefined") {
      if (newUser) {
        localStorage.setItem("user-info", JSON.stringify(newUser));
      }
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedUser = localStorage.getItem("user-info");
      if (storedUser) {
        setUserState(JSON.parse(storedUser));
      }
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userAccountData?.data && isSuccess) {
      const mapped: BrokerDetails = userAccountData.data.reduce(
        (acc: any, broker: any) => {
          acc[broker.broker_id] = {
            ...broker,
            balance: broker?.payload?.main?.USD,
          };
          return acc;
        },
        {} as BrokerDetails
      );

      setConnectedBrokers(mapped);
    }
  }, [userAccountData, balanceLoading]);

  const refreshConnectedBrokers = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["get_account_balances", user?.user_id],
    });
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        connectedBrokers,
        refreshConnectedBrokers,
        balanceLoading,
        userLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context)
    throw new Error("useUserContext must be used within a UserProvider");
  return context;
};
